/**
 * ⟦SPEC-PRODUCTEUR §3-§6 · A-66⟧ **Le producteur — le deuxième étage de l'escalier de
 * délégation.**
 *
 * Le premier étage (le marché des freelances, `freelances.ts`) retire **l'adresse** : on ne
 * joue plus les mini-jeux. Celui-ci retire **le choix de quoi faire** : le sujet, le format,
 * le moment. Le troisième (le manager, §5) retirerait le **rythme** — il n'est pas construit,
 * et l'en-tête de la spec le dit désormais.
 *
 * ── Les deux règles qui gouvernent tout ce fichier ──────────────────────────────
 *
 * **1. Il n'a AUCUN code de résolution.** Il ne calcule ni vues, ni abonnés, ni argent, ni
 * jauges : il **choisit une `Action` existante**, qui repasse par `resolve` — le même chemin
 * que le joueur. C'est ce qui rend l'état neutre vrai *par construction* et non par
 * vérification : aucune ancre de calibrage ne peut bouger, puisqu'aucune formule ne change.
 *
 * **2. Il n'a AUCUN tirage propre.** ⟦SPEC-MOTEUR §3.2 reste une liste FERMÉE de 9 purposes,
 * et ce module n'en demande pas un dixième.⟧ Le choix est un **tri lexicographique**
 * entièrement déterministe : il se lit dans un test, il s'explique au joueur en une phrase, et
 * surtout il rend l'équipe **apprenable**. Une équipe imprévisible serait une équipe qu'on ne
 * peut pas diriger, donc des consignes qui ne veulent rien dire.
 *
 * ── Ce qu'il ne décide JAMAIS ⟦§8⟧ ──────────────────────────────────────────────
 *
 * Les événements, l'arbre, les jalons, la communauté, la bibliothèque, la fin. C'est le
 * plancher, et il ne se négocie pas. **Rien ici n'automatise la fenêtre d'événement** —
 * décision de Mathieu, mot pour mot : *« je ne pense pas qu'il faut qu'il y ait des trucs qui
 * automatisent la fenêtre d'événement parce que là, sinon, tu n'as plus rien à jouer. »*
 * Déléguer libère du temps ; ça ne retire jamais une décision.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`, aucun `draw`.
 */
import { H_FAILLITE, H_JOUR } from "./clock.js";
import { hasCapability } from "./effects.js";
import { mecaniqueOuverte } from "./escalier.js";
import { etapesOuvertes, offresPourEtape } from "./freelances.js";
import { CADENCES, primeRetourManager, seuilTension } from "./manager.js";
import { SYSTEMES_TOUS_ACTIFS, systemes } from "./menage.js";
import { pauseDecouverte } from "./pause.js";
import { ATELIER_STAGES } from "./types.js";
import { STRIKES_FATAL, strikesActifs } from "./gameover.js";
// ─────────────────────────────────────────────────────────────────────────────
// §1.1 Les consignes — l'objet que le joueur règle
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§13.1 ✎ 12⟧ Les défauts. **C'est l'état neutre** : un joueur qui n'ouvre jamais l'écran de
 * réglage délègue avec ça, et `state.consignes` reste `undefined` tant qu'il n'y touche pas.
 */
export const CONSIGNES_DEFAUT = {
    editoriale: { cap: "neutre", regime: "mixte", tendance: "indifferent", budget_video_max: null },
};
/** Les consignes de la run, défauts compris. Ne jamais lire `state.consignes` en direct. */
export function consignes(state) {
    return state.consignes ?? CONSIGNES_DEFAUT;
}
// ─────────────────────────────────────────────────────────────────────────────
// L'interrupteur, la capacité, l'activité
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§9⟧ **L'interrupteur général de l'escalier** (`content/meta.json → equipe_active`).
 *
 * Éteint, `filtrerSkillsInterrupteurs` a déjà retiré le nœud du contenu chargé — mais la
 * garde est répétée ici, et ce n'est pas de la ceinture-bretelles : `laisser_tourner` doit
 * être refusée même si un état sérialisé porte encore `production_deleguee` dans ses skills
 * (une run commencée interrupteur allumé, relue interrupteur éteint). **L'interrupteur doit
 * couper l'exécution, pas seulement le chargement** — c'est la leçon de `fil.ts:591`.
 */
export function equipeActive(content) {
    return content.meta?.equipe_active === true;
}
/** Le joueur possède-t-il le nœud *Le producteur* ? */
export function producteurPossede(mods) {
    return hasCapability(mods, "production_deleguee");
}
/**
 * ⟦§1.2⟧ Le producteur travaille-t-il ? Possédé **et** pas congédié **et** pas parti faute de
 * paie. `flags.producteur_actif` absent ⇒ `true` dès que le nœud est là : acheter, c'est
 * embaucher.
 */
export function producteurActif(state, mods) {
    if (!producteurPossede(mods))
        return false;
    return state.flags.producteur_actif !== false;
}
// ─────────────────────────────────────────────────────────────────────────────
// §6.2 Le salaire — ce que l'étage coûte à entretenir
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§13.1 ✎ 3-4⟧ Les défauts, quand `content/producteur.json` est absent. */
export const SALAIRE_PRODUCTEUR_J_DEFAUT = 15;
export const PRIME_RETOUR_DEFAUT = 150;
export const JOURS_GRACE_DEFAUT = 3;
/**
 * ⟦C8 · A-120⟧ Le candidat producteur CHOISI — `state.flags.producteur_candidat_id` résolu
 * contre `content.producteur.candidats_producteur`, ou le défaut compat (`producteur.
 * producteur`, Béa Castelclic) : un état sauvegardé sans recrutement garde le comportement
 * d'aujourd'hui, au caractère près. `candidatIdOverride` l'emporte sur le drapeau — sert à
 * valider/facturer une recrue avant que `regler_equipe` n'écrive l'id (⟦A-11 §1⟧).
 */
export function producteurChoisi(state, content, candidatIdOverride) {
    const id = candidatIdOverride ?? state.flags.producteur_candidat_id;
    const candidat = id ? content.producteur?.candidats_producteur?.find((c) => c.id === id) : undefined;
    return (candidat ??
        content.producteur?.producteur ?? {
        salaire_jour: SALAIRE_PRODUCTEUR_J_DEFAUT,
        prime_retour: PRIME_RETOUR_DEFAUT,
    });
}
export function salaireProducteur(state, content, candidatIdOverride) {
    return producteurChoisi(state, content, candidatIdOverride).salaire_jour;
}
export function primeRetourProducteur(state, content, candidatIdOverride) {
    return producteurChoisi(state, content, candidatIdOverride).prime_retour;
}
export function joursGrace(content) {
    return content.producteur?.jours_grace ?? JOURS_GRACE_DEFAUT;
}
export function paierProducteur(state, content, mods) {
    if (!equipeActive(content) || !producteurActif(state, mods))
        return null;
    const salaire = salaireProducteur(state, content);
    if (state.money >= salaire) {
        state.money -= salaire;
        state.counters.producteur_jours_impayes = 0;
        return { paye: salaire, impaye: false, parti: false };
    }
    const impayes = (state.counters.producteur_jours_impayes ?? 0) + 1;
    state.counters.producteur_jours_impayes = impayes;
    const parti = impayes >= joursGrace(content);
    if (parti)
        state.flags.producteur_actif = false;
    return { paye: 0, impaye: true, parti };
}
// ─────────────────────────────────────────────────────────────────────────────
// §1.3 / §6.3 La réunion d'équipe — l'effet que l'action n'avait pas
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Les deux consignes disent-elles la même chose ? Comparaison **champ à champ**, jamais par
 * `JSON.stringify` : l'ordre des clés d'une charge utile réseau n'est pas garanti, et un faux
 * « différent » ferait payer une réunion pour rien — exactement le mensonge de prix que §6.3
 * interdit.
 */
export function memesConsignes(a, b) {
    const x = a.editoriale;
    const y = b.editoriale;
    return (x.cap === y.cap &&
        x.regime === y.regime &&
        x.tendance === y.tendance &&
        x.budget_video_max === y.budget_video_max);
}
export function appliquerReglageEquipe(state, action, content) {
    const changees = !memesConsignes(consignes(state), action.consignes);
    state.consignes = {
        editoriale: { ...action.consignes.editoriale },
        ...(action.consignes.rythme === undefined
            ? {}
            : { rythme: { ...action.consignes.rythme } }),
    };
    // ⟦C8 · A-120⟧ Le recrutement s'écrit AVANT les calculs de prime ci-dessous : recruter et
    // (r)embaucher dans le même geste facture la prime de LA recrue, jamais celle de l'ancienne
    // titulaire — `validateAction` a déjà vérifié le solde sur ce même candidat (⟦A-11 §1⟧).
    if (action.producteur_candidat_id !== undefined) {
        state.flags.producteur_candidat_id = action.producteur_candidat_id;
    }
    if (action.manager_candidat_id !== undefined) {
        state.flags.manager_candidat_id = action.manager_candidat_id;
    }
    const actifAvant = state.flags.producteur_actif !== false; // la possession est validée en amont
    let bascule = null;
    let basculeManager = null;
    let prime = 0;
    let primeManager = 0;
    if (actifAvant && !action.producteur_actif) {
        // Congédier : gratuit, 0 h, immédiat. Le nœud reste acheté — on redescend d'une marche.
        state.flags.producteur_actif = false;
        bascule = "congedie";
        // ⟦§6.3 · T-PROD-10⟧ **Congédier le producteur éteint DE FAIT le manager** (il n'a plus
        // rien à faire produire) — et le drapeau de suspension garantit qu'au retour du
        // producteur, le manager revient SANS prime : la porte de sortie n'est pas un piège.
        if (state.flags.manager_actif !== false) {
            state.flags.manager_actif = false;
            state.flags.manager_suspendu_avec_producteur = true;
            basculeManager = "suspendu";
        }
    }
    else if (!actifAvant && action.producteur_actif) {
        // Réembaucher : la prime, sinon rien — la validation a déjà refusé un solde insuffisant.
        prime = primeRetourProducteur(state, content);
        state.money -= prime;
        state.flags.producteur_actif = true;
        state.counters.producteur_jours_impayes = 0;
        bascule = "embauche";
        if (state.flags.manager_suspendu_avec_producteur === true) {
            state.flags.manager_actif = true;
            delete state.flags.manager_suspendu_avec_producteur;
            basculeManager = "revenu";
        }
    }
    // ⟦§1.3 / §6.3 · M3.29⟧ La bascule EXPLICITE du manager — après celle du producteur, pour
    // qu'un geste explicite l'emporte sur l'effet de bord. Champ absent = on n'y touche pas.
    if (action.manager_actif !== undefined) {
        const managerAvant = state.flags.manager_actif !== false;
        if (managerAvant && !action.manager_actif) {
            state.flags.manager_actif = false;
            delete state.flags.manager_suspendu_avec_producteur; // congédiement voulu, pas une suspension
            basculeManager = "congedie";
        }
        else if (!managerAvant && action.manager_actif) {
            if (state.flags.manager_suspendu_avec_producteur === true) {
                // La suspension se lève sans prime — voir le drapeau.
                state.flags.manager_actif = true;
                delete state.flags.manager_suspendu_avec_producteur;
                basculeManager = "revenu";
            }
            else {
                primeManager = primeRetourManager(state, content);
                state.money -= primeManager;
                state.flags.manager_actif = true;
                state.counters.manager_jours_impayes = 0;
                basculeManager = "embauche";
            }
        }
    }
    return {
        consignes_changees: changees,
        producteur: bascule,
        manager: basculeManager,
        prime_payee: prime,
        prime_manager_payee: primeManager,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// §4 LE PRODUCTEUR — choisir la vidéo
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§13.1 ✎ 9⟧ Les cibles d'alignement des trois caps, et le grain qui les rend myopes. */
export const CIBLE_CAP = {
    lumiere: 60,
    neutre: 0,
    chaos: -60,
};
export const GRAIN_CAP = 20;
/**
 * ⟦§4.4⟧ Le programme du régime, **au vu de la main disponible**.
 *
 * `mixte` penche volontairement vers la longue, et ce n'est pas de l'optimisation cachée :
 * ⟦A-11 §1⟧ a mesuré qu'il faut **16 667 vues/jour en shorts contre 1 111 en longues** pour
 * couvrir 5 ₵/jour. Un producteur qui ne ferait que des shorts ne se paierait jamais, et toute
 * la mécanique d'entretien (§6) perdrait son sens.
 */
export function programmeDuRegime(regime, cartesEnMain, evergreenOuvert, meilleurPotentiel) {
    switch (regime) {
        case "shorts":
            return cartesEnMain >= 1 ? { format: "short", cartes: 1, evergreen: false } : null;
        case "mixte":
            if (cartesEnMain >= 2)
                return { format: "longue", cartes: 2, evergreen: false };
            return cartesEnMain >= 1 ? { format: "short", cartes: 1, evergreen: false } : null;
        case "longues":
            // Sinon la boucle veille et attend — elle ne se rabat JAMAIS sur un short.
            return cartesEnMain >= 2 ? { format: "longue", cartes: 2, evergreen: false } : null;
        case "evergreen":
            if (evergreenOuvert && cartesEnMain >= 3 && meilleurPotentiel >= 60) {
                return { format: "longue", cartes: 3, evergreen: true };
            }
            return cartesEnMain >= 2 ? { format: "longue", cartes: 2, evergreen: false } : null;
    }
}
/**
 * ⟦§4.4⟧ Une tendance **active** couvre-t-elle ce thème ?
 *
 * 🔑 **Deuxième source d'infériorité, et la plus élégante : il n'est jamais mieux informé que
 * l'arbre du joueur.** Sans *Heure de pub* (`phase_tendance_visible`), une tendance **saturée**
 * lui ressemble à une tendance en pic — **et il publiera dessus.** Payer pour que son
 * producteur voie clair devient un vrai choix de build.
 */
function surTendanceVisible(state, sujet, voitLaPhase) {
    return state.trends.some((t) => {
        if (!t.themes.includes(sujet.theme))
            return false;
        // Aveugle à la phase : toute tendance présente compte, saturée comprise.
        return voitLaPhase ? t.phase !== "saturee" : true;
    });
}
/**
 * ⟦§4.2-§4.6⟧ **Le choix, en un tri lexicographique.**
 *
 * Aucune pondération, aucun tirage, aucun seuil caché. Chaque critère ne départage que les ex
 * æquo du précédent :
 *
 *  1. **la conformité au cap, à `GRAIN_CAP` points près** — 🔑 *premier des cinq endroits où il
 *     est délibérément moins bon qu'un joueur* : il ne distingue pas +52 de +59 ;
 *  2. **la tendance**, avec ce qu'il sait (voir `surTendanceVisible`) ;
 *  3. **l'anti-répétition** : il préfère un thème différent de `counters.last_theme`. Il
 *     connaît la règle des trois (SPEC-MOTEUR §2.4) et l'**évite** — il ne l'exploite pas ;
 *  4. **la carte la plus ancienne**, puis `subject_id` en ordre lexicographique. Départage
 *     arbitraire *et* déterministe : c'est ce qui permet de le verrouiller par un test.
 *
 * Rend `null` quand rien n'est produisible — la boucle veillera (§5.3).
 */
export function choisirProduction(state, content, mods) {
    const ligne = consignes(state).editoriale;
    const parId = new Map(content.subjects.map((s) => [s.id, s]));
    const voitLaPhase = hasCapability(mods, "phase_tendance_visible");
    const voitLePotentiel = hasCapability(mods, "potentiel_evergreen_visible");
    const candidats = [];
    for (const carte of state.inspiration) {
        const sujet = parId.get(carte.subject_id);
        if (sujet === undefined)
            continue;
        // ⟦§4.2 critère 0⟧ **L'élimination en AMONT, pas au refus.** Un thème épuisé (règle des
        // trois, SPEC-MOTEUR §2.4) rendait l'action refusée par `resolve` — et le `catch` de la
        // boucle déguisait ce refus en arrêt `tresorerie`, un motif FAUX affiché au joueur. Le
        // producteur « choisit autre chose » (§5.4) : la carte est simplement écartée du tri.
        // Même test que `themeEpuise` (actions.ts) — deux lignes, dupliquées avec leur source
        // nommée plutôt qu'importées, parce qu'`actions.ts` importe déjà ce module.
        const cooldown = state.counters.theme_cooldowns[sujet.theme];
        if (cooldown !== undefined && state.clock_h < cooldown)
            continue;
        candidats.push({
            subject_id: carte.subject_id,
            drawn_at_h: carte.drawn_at_h,
            sujet,
            surTendance: surTendanceVisible(state, sujet, voitLaPhase),
        });
    }
    if (candidats.length === 0)
        return null;
    // ⟦§4.4⟧ Il ne lit `potentiel_evergreen` que si *Analytics addict* est débloqué. Sans lui,
    // il ne sait pas qu'une carte est une evergreen — donc il n'en tentera jamais une.
    const meilleurPotentiel = voitLePotentiel
        ? Math.max(...candidats.map((c) => c.sujet.potentiel_evergreen))
        : 0;
    let programme = programmeDuRegime(ligne.regime, candidats.length, hasCapability(mods, "production_evergreen"), meilleurPotentiel);
    // ⟦§4.2 critère 0⟧ Format verrouillé : une longue avant son ouverture (SPEC-MOTEUR §2.2,
    // `format_locked`) serait refusée par `resolve`. `mixte` se rabat sur le short — c'est déjà
    // sa pente naturelle ; `longues` et `evergreen` veillent et attendent, comme leur régime
    // l'exige (« il ne se rabat JAMAIS sur un short »).
    if (programme !== null && programme.format === "longue" && !mecaniqueOuverte(state, "longue")) {
        programme = ligne.regime === "mixte" ? programmeDuRegime("shorts", candidats.length, false, 0) : null;
    }
    if (programme === null)
        return null;
    const cible = CIBLE_CAP[ligne.cap];
    // ⟦C60 · A-177⟧ Sans alignement, le cap (lumière / neutre / chaos) ne départage plus rien : tous les
    // sujets sont au même rang. Actif (le défaut), la conformité au cap d'avant, au caractère près.
    const capCompte = systemes(content).alignement;
    const rang1 = (c) => capCompte ? Math.floor(Math.abs(c.sujet.alignement - cible) / GRAIN_CAP) : 0;
    /** ⟦§4.4⟧ Croissant : 0 = ce que la consigne préfère. */
    const rang2 = (c) => {
        switch (ligne.tendance) {
            case "surfer":
                return c.surTendance ? 0 : 1;
            case "indifferent":
                return 0;
            case "intemporel":
                // Il fuit la vague ; et il ne lit le potentiel evergreen que s'il le voit.
                if (c.surTendance)
                    return 2;
                return voitLePotentiel && c.sujet.potentiel_evergreen >= 60 ? 0 : 1;
        }
    };
    const rang3 = (c) => (c.sujet.theme === state.counters.last_theme ? 1 : 0);
    const tries = [...candidats].sort((a, b) => rang1(a) - rang1(b) ||
        rang2(a) - rang2(b) ||
        rang3(a) - rang3(b) ||
        a.drawn_at_h - b.drawn_at_h ||
        a.subject_id.localeCompare(b.subject_id));
    const tete = tries[0];
    if (tete === undefined)
        return null;
    // Les cartes supplémentaires suivent le même ordre : le tri est total, donc reproductible.
    const extra = tries.slice(1, programme.cartes).map((c) => c.subject_id);
    if (extra.length < programme.cartes - 1)
        return null;
    return {
        subject_id: tete.subject_id,
        format: programme.format,
        extra_card_subjects: extra,
        evergreen_intent: programme.evergreen,
    };
}
/**
 * ⟦§4.5⟧ **La règle des ciseaux : il délègue les CINQ étapes, ou il ne produit pas.**
 *
 * Il ne joue aucun mini-jeu, n'invente aucun score, ne se rabat sur aucune moyenne. Les scores
 * envoyés sont donc **entièrement ignorés** par le pipeline — `scoresEffectifs` les remplace
 * par la livraison du freelance sur chaque étape déléguée. On les met à 0 plutôt qu'à une
 * valeur plausible : un 0 qui remonterait quelque part est un bug qu'on veut voir.
 *
 * Corollaire technique : parce que **toutes** les étapes sont déléguées, l'action ne porte
 * aucune timeline d'Atelier — `validateAtelierScore` n'a rien à valider, et la question
 * « comment le serveur valide-t-il un score qu'aucun joueur n'a joué ? » ne se pose pas.
 */
export function scoresEntierementDelegues(format) {
    const etapes = format === "short"
        ? ATELIER_STAGES.filter((s) => s !== "miniature" && s !== "promotion")
        : [...ATELIER_STAGES];
    return {
        ecriture: 0,
        tournage: 0,
        montage: 0,
        miniature: format === "short" ? null : 0,
        promotion: format === "short" ? null : 0,
        delegated: etapes,
    };
}
/**
 * ⟦§4.5⟧ Le producteur peut-il déléguer les cinq étapes ? Il lui faut `delegation_totale` —
 * c'est pourquoi *Le Studio* est en **prérequis** du nœud, et le prérequis n'est pas
 * décoratif : il est la condition de fonctionnement.
 */
export function peutDeleguerTout(state, mods) {
    // ⟦A-78⟧ La porte du marché est le Grand Saut — les cinq étapes s'ouvrent ensemble. Le
    // prérequis `le_studio` du nœud producteur reste, lui, un choix d'arbre (§2.1) : il
    // n'ouvre plus le marché, il l'approfondit (une offre de plus par étape).
    return etapesOuvertes(state, mods).length === ATELIER_STAGES.length;
}
export const RYTHME_SANS_MANAGER = {
    manager: false,
    cadence: "raisonnable",
    repos: "jamais",
    tension: false,
};
// ─────────────────────────────────────────────────────────────────────────────
// §4.5 · A-92 Le budget d'UNE vidéo — le coussin, la part, le plafond du joueur
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§4.5 · A-92⟧ **Le coussin : ce que le producteur n'engage JAMAIS.** `SEUIL_TENSION_J`
 * jours de lignes fixes — les charges du jour et les salaires des étages qui travaillent
 * (§6.2, `seuilTension`). C'est le seuil que §5.4bis appelle déjà *tension de trésorerie*
 * pour le manager, **le même nombre exprès** : sous lui, le manager replie sur le moins
 * cher ; le producteur seul, lui, n'y descend pas par ses achats. Un producteur qui met la
 * chaîne à la rue n'est pas un producteur.
 *
 * **Sous tension, avec manager, le coussin est levé** (`budgetVideo`) : c'est la lettre de
 * §5.4bis — *« il replie sur le moins cher qui rentre et continue ; il n'interrompt que si
 * même le moins cher ne passe pas »*. Le manager fait tourner la boutique dans la zone où le
 * producteur seul s'arrête — c'est une partie de ce que ses 16 PC achètent. Et parce que le
 * coussin et le seuil de tension sont le même nombre, **aucune bande de caisse ne tombe
 * entre les deux règles** : au-dessus, le coussin ; en dessous, le repli.
 *
 * Mesuré avant ce coussin ⟦A-80 §4.2⟧ : la caisse vidée « jusqu'au sol », `tresorerie` dans
 * **113 arrêts sur 298 appels** (T-PROD-13), puis le retour chez les parents et une équipe
 * qui produit des nulles depuis la chambre d'enfant.
 */
export function coussinProducteur(state, content, managerTravaille) {
    return seuilTension(state, content, managerTravaille);
}
/**
 * ⟦§4.5 · A-92⟧ **« Payé et couvert »** ✎ — le diviseur de ce qu'il engage sur UNE vidéo : au
 * plus `1 / COUVERTURE_VIDEO` de ce qui dépasse le coussin. À 2, une fois la vidéo payée il
 * reste de quoi en payer une seconde au même prix. C'est le principe que le harnais applique
 * déjà à la reprise d'un studio (*« payé ET couvert, jamais dès que c'est payable »*), et
 * c'est ce qui fait de la dépense une boucle de rétroaction : la caisse monte, il embauche
 * mieux ; elle baisse, il embauche moins bien — jamais « tout ce qu'il y a ».
 */
export const COUVERTURE_VIDEO = 2;
/**
 * ⟦§3.2 / §4.5 · A-92⟧ **Le budget d'une vidéo, en trois bornes** :
 *
 *  1. **le coussin** — jamais sous `coussinProducteur` ;
 *  2. **la part** — au plus `disponible / COUVERTURE_VIDEO` … **mais jamais moins que
 *     l'équipe la moins chère** tant qu'elle tient au-dessus du coussin : la part ne sert pas
 *     à refuser une vidéo payable, elle sert à ne pas la payer trop cher ;
 *  3. **le plafond du joueur** — `budget_video_max` borne tout : s'il est sous l'équipe la
 *     moins chère, *« il ne produit pas »* (§3.2), au mot près.
 *
 * `prixMin` est le devis de l'équipe la moins chère qui pourvoit toutes les étapes, ou
 * `null` si une étape n'a personne — le budget vaut alors ce qu'il vaut, et aucune équipe ne
 * rentrera dedans.
 */
export function budgetVideo(state, content, plafondJoueur, prixMin, rythme = RYTHME_SANS_MANAGER) {
    // ⟦§5.4bis⟧ Sous tension, le manager lève le coussin : « le moins cher qui rentre ».
    const coussin = rythme.manager && rythme.tension ? 0 : coussinProducteur(state, content, rythme.manager);
    const disponible = Math.max(0, Math.floor(state.money) - coussin);
    const part = Math.floor(disponible / COUVERTURE_VIDEO);
    const budget = Math.min(plafondJoueur ?? Number.POSITIVE_INFINITY, disponible, Math.max(part, prixMin ?? 0));
    return { coussin, disponible, part, budget };
}
function compareIds(a, b) {
    for (let i = 0; i < a.length && i < b.length; i++) {
        const d = a[i].localeCompare(b[i]);
        if (d !== 0)
            return d;
    }
    return a.length - b.length;
}
/**
 * ⟦§4.5 · A-92⟧ **La moins mauvaise équipe qui rentre** — un freelance par étape, *tous
 * ensemble* sous le budget.
 *
 * L'ordre de préférence est celui du §4.5, porté de la personne à l'équipe : **la somme des
 * cotes la plus haute**, puis la somme des variances la plus basse (il achète de la
 * régularité), puis le devis le plus bas, puis les ids dans l'ordre des étapes. Aucune
 * pondération : il ne connaît pas les poids de `scoreQualite` (§7.1, « aucun coefficient
 * caché »). `repliMoinsCher` (§5.4bis, le manager sous tension) retourne les portes : le
 * devis le plus bas d'abord.
 *
 * **Pourquoi une équipe, et plus cinq choix de suite.** ⟦A-66⟧ consommait le budget étape par
 * étape « à la meilleure cote qui rentre » : l'écriture prenait le pro au prix fort, et la
 * promotion se retrouvait impayable — sous un `budget_video_max` que l'équipe la plus chère
 * dépassait, **la vidéo échouait au lieu de se faire moins chère** ⟦A-80 §4.2⟧. Le cadran ne
 * bornait rien. Ici, le parcours est exhaustif — au plus `OFFRES_PAR_ETAPE` personnes par
 * étape, pépites écartées, donc 2⁵ combinaisons — et **déterministe** : aucun tirage, et
 * l'ordre d'examen ne change pas le résultat (le comparateur est total).
 *
 * Rend `null` si aucune combinaison ne tient dans le budget — y compris quand une étape n'a
 * personne : la règle des ciseaux, « il délègue les cinq étapes ou il ne produit pas ».
 */
export function equipeSousBudget(marche, budget, repliMoinsCher = false) {
    const mieux = (a, b) => repliMoinsCher
        ? a.prix - b.prix || b.cote - a.cote || a.variance - b.variance || compareIds(a.ids, b.ids)
        : b.cote - a.cote || a.variance - b.variance || a.prix - b.prix || compareIds(a.ids, b.ids);
    let meilleure = null;
    const parcourir = (rang, cumul) => {
        if (rang === marche.length) {
            if (meilleure === null || mieux(cumul, meilleure) < 0)
                meilleure = cumul;
            return;
        }
        for (const o of marche[rang][1]) {
            if (o.pepite)
                continue; // ⟦§7.2 n° 3⟧ il ne repère JAMAIS une pépite
            const prix = cumul.prix + o.prix;
            if (prix > budget)
                continue;
            parcourir(rang + 1, {
                cote: cumul.cote + o.cote,
                variance: cumul.variance + o.variance,
                prix,
                ids: [...cumul.ids, o.id],
            });
        }
    };
    parcourir(0, { cote: 0, variance: 0, prix: 0, ids: [] });
    if (meilleure === null)
        return null;
    const retenue = meilleure;
    const embauches = {};
    marche.forEach(([stage], i) => {
        embauches[stage] = retenue.ids[i];
    });
    return { embauches, devis: retenue.prix };
}
/**
 * L'action de production que le producteur émet.
 *
 * ⟦§4.5⟧ **Le choix du freelance est une décision, donc elle relève de ce module** : chaque
 * étape déléguée porte son `freelances[stage]` explicite — l'équipe de `equipeSousBudget`,
 * jamais une pépite. Avant ce choix explicite, le champ restait vide et le pipeline
 * retombait sur le défaut JOUEUR (le moins cher) : la règle livrée était l'exact opposé de
 * la règle spécifiée.
 *
 * ⟦§4.2 critère 0 / §3.2 · A-92⟧ **Le budget mord ici, et il n'est plus « la caisse
 * entière »** : `budgetVideo` — le coussin, la part payée et couverte, le plafond du joueur —
 * puis la moins mauvaise équipe qui y tient. `budget_video_max` devient un vrai cadran : sous
 * le prix de l'équipe la plus chère, il rend l'équipe moins chère ; sous celui de la moins
 * chère, « il ne produit pas ».
 *
 * ⟦§5.4bis⟧ `rythme` : le manager (étage 3) compte dans le coussin (son salaire est une ligne
 * fixe) et, sous tension, retourne les portes du tri — même règle, jamais une deuxième.
 */
export function actionProduction(state, content, mods, seed, rythme = RYTHME_SANS_MANAGER) {
    const RIEN = { action: null, impayable: false, devis: 0, budget: 0 };
    if (!peutDeleguerTout(state, mods))
        return RIEN;
    const choix = choisirProduction(state, content, mods);
    if (choix === null)
        return RIEN;
    const scores = scoresEntierementDelegues(choix.format);
    // Le marché de chaque étape déléguée, pépites écartées (§4.5 / §7.2 n° 3) — trié par prix
    // croissant puis id par `offresPourEtape` : la première offre est la moins chère.
    const marche = scores.delegated.map((stage) => [
        stage,
        // ⟦A-78 §2⟧ Avec les mods : le marché du producteur est celui que la facture
        // appliquera (`publish.ts` embauche sur la profondeur d'offre de l'arbre possédé),
        // et `le_studio` est de toute façon son prérequis.
        offresPourEtape(state, content, seed, stage, undefined, mods).filter((o) => !o.pepite),
    ]);
    const prixMin = marche.reduce((acc, [, offres]) => {
        const moinsCher = offres[0]?.prix;
        return acc === null || moinsCher === undefined ? null : acc + moinsCher;
    }, 0);
    const b = budgetVideo(state, content, consignes(state).editoriale.budget_video_max, prixMin, rythme);
    const equipe = equipeSousBudget(marche, b.budget, rythme.manager && rythme.tension);
    if (equipe === null) {
        // Aucune équipe ne tient dans le budget ⇒ pas de vidéo (la règle des ciseaux), et le
        // motif est dit : `tresorerie`, déclencheur `delegation_impayable` (§5.4).
        return { action: null, impayable: true, devis: 0, budget: b.budget };
    }
    scores.freelances = equipe.embauches;
    const devis = equipe.devis;
    if (choix.format === "short") {
        return {
            action: { type: "produce_video", format: "short", subject_id: choix.subject_id, scores },
            impayable: false,
            devis,
            budget: b.budget,
        };
    }
    return {
        action: {
            type: "produce_video",
            format: "longue",
            subject_id: choix.subject_id,
            evergreen_intent: choix.evergreen_intent,
            extra_card_subjects: choix.extra_card_subjects,
            scores,
        },
        impayable: false,
        devis,
        budget: b.budget,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// §5.4 La liste FERMÉE des interruptions
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§13.1 ✎ 10⟧ Les planchers historiques de la cadence `raisonnable` — conservés sous leur
 * nom d'⟦A-66⟧ : sans manager, la boucle tourne toujours sur eux (voir `CADENCES`).
 */
export const SEUIL_SM_DELEGATION = CADENCES.raisonnable.seuil_sm;
export const MARGE_ENERGIE_DELEGATION = CADENCES.raisonnable.marge_energie;
/**
 * ⟦§5.4bis⟧ La santé mentale sous le plancher est-elle ABSORBABLE — le manager a-t-il un
 * geste à jouer ? `therapie` : il faut de quoi payer la séance. `tout` : il faut que la pause
 * annoncée soit découverte ⟦A-2⟧ — annoncer, puis dormir, et la pause s'engage au sommeil.
 *
 * ⟦C60 bis · A-177⟧ **La séance se paie en temps et en énergie, plus en Clicks** : ce que `sys` (les
 * réglages du contenu) fait payer, le manager doit pouvoir le payer. Avant : 150 ₵ (les Clicks
 * d'avant, `sys` par défaut) ; maintenant : l'énergie de la séance, et des Clicks seulement si le
 * contenu en demande encore. Sans `sys`, la règle d'avant.
 */
export function absorptionSM(state, rythme, sys = SYSTEMES_TOUS_ACTIFS) {
    if (!rythme.manager)
        return false;
    switch (rythme.repos) {
        case "jamais":
            return false;
        case "therapie":
            return seancePayable(state, sys);
        case "tout":
            return pauseDecouverte(state) || seancePayable(state, sys);
    }
}
/** La séance de thérapie est payable : les Clicks que le contenu lui laisse, et son énergie. */
function seancePayable(state, sys) {
    return state.money >= sys.therapie_clicks && state.energy >= sys.therapie_energie;
}
/**
 * ⟦C62 bis · A-179⟧ **Une énergie trop basse pour la séance n'est pas un motif d'arrêt : l'équipe DORT d'abord** (une nuit rend
 * l'énergie et dix points de santé mentale), puis la séance se joue. C'est le « dormir » de « dormir et souffler » : sans lui, un
 * joueur qui rend les clés vidé (énergie et santé mentale basses ensemble, le cas ordinaire) retombait sur « à bout » malgré le
 * repos automatique. Vrai quand le manager travaille, que le repos n'est pas « jamais », que les Clicks que le contenu ferait
 * encore payer sont là (ce qui reste un motif d'arrêt) et que seule l'énergie manque. `absorptionSM` garde son contrat : « le
 * manager a un geste payable MAINTENANT ».
 */
export function dormirAvantLaSeance(state, rythme, sys = SYSTEMES_TOUS_ACTIFS) {
    if (!rythme.manager || rythme.repos === "jamais")
        return false;
    return state.money >= sys.therapie_clicks && state.energy < sys.therapie_energie;
}
/**
 * ⟦§5.4⟧ Le premier motif d'arrêt applicable, ou `null` si le jeu peut continuer sans le
 * joueur. L'ordre est celui de la table, et il n'est pas indifférent : `fin` d'abord (une run
 * terminée n'a plus rien à déléguer), `evenement` ensuite (il bloque tout, arbitrage A24).
 */
export function premierMotifDArret(state, content, mods, jalonJouable, noeudAchetable, rythme = RYTHME_SANS_MANAGER, 
/**
 * ⟦§5.4 · A-81⟧ L'action qui vient d'être jouée a franchi un palier (event `milestone`).
 * Aucun compteur dans l'état : la boucle le lit sur les événements du pas précédent, et
 * la relance après l'arrêt repart sans lui — c'est le « se répète : oui » de la table.
 * Défaut `false` = l'état neutre pour tout autre appelant.
 */
palierFranchi = false, 
/**
 * ⟦§5.4 · A-92⟧ L'action qui vient d'être jouée a OUVERT l'arc de la chute (warning
 * `arc_chute_ouvert`, `majArcDeLaChute`). Même lecture que `palierFranchi` : sur les
 * événements du pas précédent, aucun champ d'état — voir le commentaire de `peril`.
 */
arcOuvert = false) {
    if (state.status !== "active")
        return "fin";
    if (state.pending_event !== null)
        return "evenement";
    // ⟦§5.4 · A-81⟧ `palier` : un palier vient d'être franchi et il a versé ses +2 PC — « placer
    // les points » est une décision, et elle passe AVANT `competence` dans la table. Il était
    // replié sur `competence` faute d'un signal ; le signal, c'est l'événement `milestone` de
    // l'action qui vient d'être jouée, relu par la boucle (`index.ts`) — pas un champ d'état.
    if (palierFranchi)
        return "palier";
    if (noeudAchetable)
        return "competence";
    if (jalonJouable)
        return "jalon";
    // ⟦§5.4⟧ `peril` — il **s'arrête** devant le danger, il n'y **réagit** jamais. Rendre les
    // clés est sa seule réaction : c'est la cinquième source d'infériorité, et elle ne coûte
    // aucune ligne de code de plus que ce test.
    //
    // ⟦A-81⟧ Les QUATRE déclencheurs de la table, plus seulement deux : le recompte du 20/08
    // avait trouvé « la faillite à J−7 et le 2ᵉ strike actif ne rendent pas la main : le
    // producteur continue à publier pendant que la chaîne coule ».
    //
    // ⟦A-92⟧ **L'arc de la chute : `peril` quand il S'OUVRE sous délégation, plus à chaque
    // appel tant qu'il est ouvert.** Trois faits, mesurés sur les délégants `chaos` ⟦A-80 §4.1⟧ :
    // (1) l'arc seul ne tue pas — seules des excuses ratées PENDANT l'arc le font
    // (`verifierGameOver`, gameover.ts), et le producteur ne joue jamais les excuses ; (2) rien
    // dans ce que le producteur fait ne referme l'arc (réputation > 25 ou excuses réussies :
    // deux décisions du joueur) ; (3) sur une ligne chaos, la réputation est structurellement
    // sous 10 (érosion −1/jour à image ≤ −40, SPEC-MOTEUR §7.2) — l'arc reste ouvert pour le
    // reste de la partie. Lire l'ÉTAT du drapeau rendait donc `peril` chaque jour, à 0 action,
    // sans qu'aucune décision ne puisse y répondre : **593 arrêts sur 967 appels, 3 083 journées
    // verrouillées**. C'est la violation littérale du critère du §5.1 (« toute interruption doit
    // appeler une décision »), et le même défaut que `competence` avant ⟦A-81⟧ : « la
    // répétition immédiate est un blocage, pas une relance ». Même remède que `palier` : le
    // FAIT (l'ouverture, un warning de l'action jouée) arrête la boucle ; l'état, non. Le joueur
    // est prévenu au moment où ça arrive — l'ouverture est un événement plein écran et l'UI
    // affiche l'arc actif (SPEC-MOTEUR §11.2) — et relancer est SA décision. Les trois autres
    // déclencheurs restent lus sur l'état : ce sont des comptes à rebours mortels que publier
    // peut précipiter (faillite, strike) ou que seul un remède lève (burn-out).
    if (arcOuvert)
        return "peril";
    if (state.flags.dernier_avertissement_burnout === true)
        return "peril";
    if (enPerilDeFaillite(state))
        return "peril";
    if (strikesActifs(state).length >= STRIKES_FATAL - 1)
        return "peril";
    // ⟦§5.3 / §5.4bis⟧ Le plancher de SM est celui de la CADENCE ; sous le plancher, le manager
    // absorbe selon `repos` — s'il a réellement un geste à jouer (sinon on rend la main, on ne
    // tourne pas en rond).
    if (state.mental < CADENCES[rythme.cadence].seuil_sm) {
        const sys = systemes(content);
        if (!absorptionSM(state, rythme, sys) && !dormirAvantLaSeance(state, rythme, sys))
            return "sante_mentale";
        // absorbable : pas un motif — la boucle jouera le soin (§5.3 priorité 1).
    }
    // ⟦§5.4 / §6.2⟧ `tresorerie` — un jour impayé, ou plus de quoi payer une vidéo déléguée.
    // ⟦§5.4bis⟧ Avec manager, ces deux lignes sont ABSORBÉES : il replie sur le moins cher et
    // continue — il n'interrompt que si même le moins cher ne passe pas (le `impayable` de
    // `actionProduction`, rendu par `prochaineActionEquipe`).
    if (!rythme.manager) {
        if ((state.counters.producteur_jours_impayes ?? 0) > 0)
            return "tresorerie";
        if (state.money < salaireProducteur(state, content))
            return "tresorerie";
    }
    return null;
}
/**
 * ⟦§5.4 · A-81⟧ « Faillite J−7 » : dans le rouge depuis au moins `H_FAILLITE − 7 jours` — c'est
 * le DERNIER jalon d'alerte de `majFaillite` (`gameover.ts`, `FAILLITE_ALERTES_H[2]`), celui
 * qui télégraphie « 7 jours avant la faillite ». Même lecture de l'état que l'alerte, donc
 * l'arrêt tombe le jour où le joueur reçoit l'avertissement — jamais avant, jamais après.
 * `money_negative_since_h` est `null` avant le Grand Saut et hors du rouge : l'état neutre.
 */
export const H_PERIL_FAILLITE = H_FAILLITE - 7 * H_JOUR;
export function enPerilDeFaillite(state) {
    const depuis = state.counters.money_negative_since_h;
    return depuis !== null && state.clock_h - depuis >= H_PERIL_FAILLITE;
}
/**
 * ⟦§5.5 · A-81⟧ **La liste FERMÉE des faits du récapitulatif** — les `EngineEvent.kind`
 * conservés un par un, dans l'ordre, par `laisser_tourner`. Ce qui n'y est pas est AGRÉGÉ par
 * cause dans `jauges` / `argent_lignes` (`views_paid`, `rent_paid`, `subs_lost`) ou n'est pas
 * un fait (`ending_chosen` ne peut pas arriver sous délégation). Ajouter un kind ici est un
 * acte de spec (§5.5), pas de code.
 */
export const FAITS_DELEGUES = [
    "video_published",
    "viral",
    "milestone",
    "jalon_done",
    "event_drawn",
    "event_resolved",
    "warning",
    "crisis_started",
    "crisis_ended",
    "level_up",
    "trend_changed",
    "game_over",
    "victory",
];
export function estFaitDelegue(kind) {
    return FAITS_DELEGUES.includes(kind);
}
export function prochaineActionEquipe(state, content, mods, seed, coutEnergieDe, rythme = RYTHME_SANS_MANAGER) {
    // ⟦§5.3 priorité 1 · M3.29⟧ **SM sous le plancher de la cadence** : le geste du MANAGER,
    // selon `repos`. `premierMotifDArret` a déjà rendu la main quand rien n'était jouable.
    if (state.mental < CADENCES[rythme.cadence].seuil_sm) {
        const sys = systemes(content);
        if (absorptionSM(state, rythme, sys)) {
            if (rythme.repos === "tout" && pauseDecouverte(state)) {
                // Annoncer, puis dormir : la pause annoncée s'engage au sommeil suivant ⟦A-2 point 4⟧.
                // ⟦C62 ter · A-179⟧ **L'annonce n'écrit qu'une INTENTION** (`counters.pause_intention_h`) : `pause_announced` n'est posé
                // qu'au sommeil (`demarrerPauseAuSommeil`). Tester `pause_announced` seul faisait annoncer la pause à chaque tour, sans
                // jamais dormir (1 h et 4 ⚡ par annonce, la santé mentale brûlée en dépassement, la run au Fantôme). Une pause annoncée
                // OU engagée appelle le sommeil, jamais une seconde annonce ; et annoncer prend de l'énergie : à plat, on dort d'abord.
                const annoncee = state.pause_announced != null || state.counters.pause_intention_h != null;
                if (annoncee)
                    return { type: "action", action: { type: "sleep" } };
                if (state.energy < coutEnergieDe({ type: "annoncer_pause" }))
                    return { type: "action", action: { type: "sleep" } };
                return { type: "action", action: { type: "annoncer_pause" } };
            }
            // `therapie`, et le repli de `tout` quand la pause n'est pas découverte mais l'argent est là.
            return { type: "action", action: { type: "therapie" } };
        }
        // ⟦C62 bis · A-179⟧ La séance prend de l'énergie : à plat, l'équipe dort d'abord (la séance suivra).
        if (dormirAvantLaSeance(state, rythme, sys))
            return { type: "action", action: { type: "sleep" } };
    }
    // La production envisagée ENSUITE : c'est son coût réel qui fixe le plancher d'énergie.
    // ⟦§5.3, recompte 20/08⟧ La marge était appliquée à côté — `coutEnergie = 0` en dur chez
    // l'appelant, donc un plancher à 20 au lieu de « coût + 20 » : le producteur lançait des
    // vidéos qu'il ne pouvait pas payer en énergie.
    // ⟦§5.4bis⟧ Le repli sur le moins cher est la faiblesse du MANAGER sous tension — jamais
    // celle du producteur seul.
    const marge = CADENCES[rythme.cadence].marge_energie;
    const production = actionProduction(state, content, mods, seed, rythme);
    if (production.impayable)
        return { type: "arret", motif: "tresorerie" };
    if (production.action !== null) {
        if (state.energy < coutEnergieDe(production.action) + marge) {
            return { type: "action", action: { type: "sleep" } };
        }
        return { type: "action", action: production.action };
    }
    // Rien à produire (cartes, régime qui attend) : dormir si à plat, sinon veiller.
    if (state.energy < Math.max(marge, 20))
        return { type: "action", action: { type: "sleep" } };
    return { type: "action", action: { type: "veille" } };
}
/** ⟦§13.1 ✎ 5⟧ Les deux bornes de la boucle. */
export const JOURS_MAX_DELEGUES = 30;
export const MAX_ACTIONS_DELEGUEES = 120;
/**
 * Le plafond d'horloge d'une délégation.
 *
 * ⚠️ **C'est un plancher de sortie, pas un plafond de durée — et l'écart est structurel.**
 * La boucle teste `clock_h < plafond_h` **avant** de jouer, mais **une action est atomique** :
 * une longue coûte 72 h ⟦A-21⟧, une evergreen 96 h. Demander « un jour » rend donc *une action*,
 * qui peut valoir trois jours. Mesuré : `jusqu_a_jours: 1` ⇒ 1 action, 72 h, 3 jours.
 *
 * On ne peut pas faire mieux sans casser quelque chose de plus important :
 *  - **couper une action en deux** est impossible (et n'aurait aucun sens de jeu) ;
 *  - **refuser de commencer une action qui dépasserait** rendrait le cran « un jour »
 *    **incapable de produire quoi que ce soit** — zéro action, la main rendue aussitôt, donc
 *    exactement l'impasse que `counters.equipe_signales` existe pour éviter ailleurs.
 *
 * **L'invariant réel, et il est vérifié par test** : aucune action ne *commence* après le
 * plafond. Le dépassement est donc borné par le coût d'**une seule** action.
 *
 * 🔑 Conséquence pour l'interface, et elle n'est pas cosmétique : le cran ne peut pas
 * s'appeler « un jour » sans mentir au joueur. Il s'appelle « une action ».
 */
export function plafondDelegation(state, jusquACeJours) {
    return state.clock_h + (jusquACeJours ?? JOURS_MAX_DELEGUES) * H_JOUR;
}
// ─────────────────────────────────────────────────────────────────────────────
// §5.5 Le récapitulatif — ce que la délégation rend
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§5.5⟧ **Ce que le joueur regarde monter, jour par jour.** Le moteur est appelé une fois et
 * rend un état ; « pendant » veut donc dire « à l'arrivée ». Ce que le client peut faire — et
 * doit faire — c'est **rejouer les jours écoulés en animant les compteurs**. Voilà le matériau.
 *
 * **Borné par construction** ⟦A38⟧ : `jours_detail.length ≤ JOURS_MAX_DELEGUES`, et
 * `actions ≤ MAX_ACTIONS_DELEGUEES`. La charge utile de l'API ne peut donc pas exploser.
 */
/** Un instantané de jauges, pour la ligne du jour. */
export function instantane(state, videos) {
    return {
        jour: Math.floor(state.clock_h / H_JOUR),
        abonnes: state.subscribers,
        argent: Math.round(state.money),
        energie: Math.round(state.energy),
        sm: Math.round(state.mental),
        videos,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 La porte d'abonnés
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§2.2⟧ Le seuil d'abonnés d'un nœud, ou `null`. Lecture **structurelle** : `Skill` (Zod) le
 * satisfait sans conversion, et `BuyableSkill` n'a pas à connaître le champ.
 */
export function porteAbonnes(skill) {
    return typeof skill.abonnes_min === "number" ? skill.abonnes_min : null;
}
/** Le nœud est-il gardé pour cet état ? */
export function porteFermee(state, skill) {
    const seuil = porteAbonnes(skill);
    return seuil !== null && state.subscribers < seuil;
}
/**
 * ⟦§5.4⟧ Y a-t-il un nœud **réellement achetable** ? C'est ce filtre qui empêche la
 * délégation d'interrompre 45 fois par run : une montée de niveau (+1 PC) n'achète presque
 * rien, et interrompre dessus serait la définition du jeu passif. On n'interrompt que quand
 * un nœud passe **toutes** ses conditions — prérequis, porte d'abonnés, PC, Clicks.
 */
export function noeudAchetableMaintenant(state, skills) {
    for (const s of skills) {
        if (state.skills.includes(s.id))
            continue;
        if (porteFermee(state, s))
            continue;
        if (s.prerequis.some((p) => !state.skills.includes(p)))
            continue;
        if (state.pc < s.cout_pc)
            continue;
        if (s.cout_euros > 0 && state.money < s.cout_euros)
            continue;
        return s.id;
    }
    return null;
}
//# sourceMappingURL=producteur.js.map